<template>
  <UIInputSelect v-model="criteriaComparison" :size :data :disabled />
  <template v-if="isFieldVisible">
    <UIInputAutocomplete
      v-if="mode === 'autocomplete'"
      v-model="criteriaValue"
      v-bind="{ size }"
      :data="periodsList"
      :disabled
      @ready="el => el.focus()"
    />
    <UIInputDate
      v-if="mode === 'input'"
      v-model="criteriaValue"
      v-bind="{ size }"
      inline-popover
      :disabled
    />
  </template>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeMount, watch } from 'vue'

import {
  FilterComparison,
  I18nTranslate,
  InputSize,
  TransactionRuleCriteria,
} from '@types'

import { FILTER_LAST_PERIODS } from '@/const'

import { getCurrentDate } from '@/helpers/dates'
import { getComparisonData } from '@/components/UI/Filter/components/utils/helpers'

import { UIInputAutocomplete, UIInputDate, UIInputSelect } from '@ui/fields'

type Props = {
  disabled?: boolean
}

defineProps<Props>()

const modelValue = defineModel<TransactionRuleCriteria['params'][number]>({
  required: true,
})

const size = inject<InputSize>('size')
const t = inject<I18nTranslate>('t', (data: string) => data)

const criteriaComparison = computed({
  get() {
    return modelValue.value.comparison
  },
  set(comparison) {
    modelValue.value = {
      ...modelValue.value,
      comparison,
    }
  },
})

const criteriaValue = computed({
  get() {
    return modelValue.value.value
  },
  set(value) {
    modelValue.value = {
      ...modelValue.value,
      value,
    }
  },
})

const data = computed(() =>
  getComparisonData({ isDate: true }).map(value => ({
    value,
    label: value,
  })),
)

const mode = computed(() => {
  if (
    criteriaComparison.value &&
    [FilterComparison.LAST].includes(criteriaComparison.value)
  ) {
    return 'autocomplete'
  } else {
    return 'input'
  }
})

const periodsList = computed(() => {
  return FILTER_LAST_PERIODS.map(value => ({
    value: value.toString(),
    label: `${value} ${t('days')}`,
  }))
})

const isFieldVisible = computed(
  () =>
    criteriaComparison.value &&
    ![FilterComparison.EMPTY, FilterComparison.NOTEMPTY].includes(
      criteriaComparison.value,
    ),
)

onBeforeMount(async () => {
  if (!criteriaComparison.value) {
    criteriaComparison.value = FilterComparison.EQ
  }
})

watch(criteriaComparison, value => {
  switch (value) {
    case FilterComparison.LAST:
      criteriaValue.value = FILTER_LAST_PERIODS[0]
      break
    case FilterComparison.EMPTY:
    case FilterComparison.NOTEMPTY:
      criteriaValue.value = undefined
      break
    default:
      criteriaValue.value = getCurrentDate()
      break
  }
})
</script>
